import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomainManager } from '../../config';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const MinimalVariant = ({ heroContent, brand }) => {
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  const { domainConfig, cta } = useTheme();
  const domainManager = useDomainManager(brand?.id);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  // Get domain-specific features content using domain manager
  const getDomainFeatures = () => {
    const features = domainManager.getFeatures();
    if (features && features.items && features.items.length > 0) {
      return {
        title: features.title,
        subtitle: features.subtitle,
        description: features.description,
        features: features.items
      };
    }
    
    // Fallback to hero content features if no domain features configured
    return {
      title: 'Why Choose Us',
      subtitle: 'Simple, effective solutions for your business needs',
      description: 'Professional solutions designed to meet your specific requirements with minimal complexity.',
      features: heroContent.features || []
    };
  };

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;
  const featuresData = getDomainFeatures();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Minimal Design */}
      <section className="pt-20 pb-32">
        <div className="max-w-6xl mx-auto px-6">
          {/* Brand Badge */}
          <div className="mb-8">
            <span className="inline-block px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-full">
              {brand.name}
            </span>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-5xl lg:text-6xl font-light text-gray-900 leading-tight mb-6">
                  {h1Title || title}
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed font-light">
                  {subtitle}
                </p>
              </div>

              {/* Features - Minimal List */}
              <div className="space-y-4">
                {featuresData.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-700 font-light">
                      {typeof feature === 'string' ? feature : feature.title}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons - Minimal Style */}
              <div className="flex space-x-4 pt-8">
                {renderCtaButton(cta?.primary, 'Request Demo', 'px-8 py-3 bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors duration-200', domainConfig?.shopLink)}
                {renderCtaButton(cta?.secondary, 'Buy Now', 'px-8 py-3 border border-gray-300 text-gray-700 font-medium hover:border-gray-400 transition-colors duration-200', domainConfig?.shopLink)}
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image */}
              <div className="relative">
                <img 
                  ref={mainImageRef}
                  src={mainImage} 
                  alt={title}
                  className="w-full h-64 md:h-96 object-cover rounded-lg"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                />
              </div>
              
              {/* Secondary Image - Floating */}
              <div className="absolute -bottom-8 -left-8 w-48 h-32">
                <img 
                  ref={secondaryImageRef}
                  src={secondaryImage} 
                  alt={`${title} - Secondary`}
                  className="w-full h-full object-cover rounded-lg shadow-lg"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Minimal */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-light text-gray-900 mb-2">50,000+</div>
              <div className="text-sm text-gray-600">Global Customers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-light text-gray-900 mb-2">80 billion</div>
              <div className="text-sm text-gray-600">Data points daily</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-light text-gray-900 mb-2">4.7 million+</div>
              <div className="text-sm text-gray-600">Vehicle Subscriptions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-light text-gray-900 mb-2">25+ years</div>
              <div className="text-sm text-gray-600">Innovation</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Minimal Cards */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{featuresData.title}</h2>
            <p className="text-gray-600 font-light">{featuresData.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuresData.features.map((feature, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto flex items-center justify-center">
                  <div className="w-6 h-6 bg-gray-400 rounded-full"></div>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {typeof feature === 'string' ? feature : feature.title}
                </h3>
                <p className="text-gray-600 font-light text-sm leading-relaxed">
                  {typeof feature === 'string' 
                    ? 'Professional solution designed to meet your specific requirements with minimal complexity.'
                    : feature.description || 'Professional solution designed to meet your specific requirements with minimal complexity.'
                  }
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MinimalVariant;
